import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import React from 'react'
import LoadingState from 'src/app/BulkInvite/components/LoadingState'
import ReviewTable, {
  ReviewRowModel,
} from 'src/app/BulkInvite/components/ReviewTable'
import { useBulkInvite } from 'src/app/BulkInvite/context/BulkInviteContext'
import * as style from 'src/app/BulkInvite/steps/ReviewStep.style'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'
import SvgAlertCircled from 'src/components/Svgs/SvgAlertCircled'
import SvgCheckCircled from 'src/components/Svgs/SvgCheckCircled'
import SvgEmail from 'src/components/Svgs/SvgEmail'
import {
  BulkInviteResponse,
  BulkInviteRowResponse,
  BulkInviteSummary,
} from 'src/queries/bulkInvite/bulkInvite.models'

const EMPTY_SUMMARY: BulkInviteSummary = {
  validNetNew: 0,
  existingMember: 0,
  pendingInvite: 0,
  duplicate: 0,
  invalid: 0,
  clash: 0,
  notInFile: 0,
  sent: 0,
  failed: 0,
}

// Rows shown in the Review table: CSV-sourced rows only. INFO_NOT_IN_FILE rows
// (org members who aren't in the uploaded CSV) are excluded entirely — they
// carry sourceType ORG_MEMBER_NOT_IN_FILE, and the category check guards the
// same set defensively.
const visibleCsvRows = (preview: BulkInviteResponse): BulkInviteRowResponse[] =>
  preview.rows.filter(
    (row) =>
      row.sourceType === 'CSV_ROW' &&
      row.previewCategory !== 'INFO_NOT_IN_FILE',
  )

const ReviewStep: React.FC = () => {
  const {
    preview,
    availableInviteRoles,
    getSelectedInviteRole,
    setSelectedInviteRole,
    sendError,
    sendErrorDetail,
  } = useBulkInvite()
  const strings = useBulkInviteStrings()

  // The Review step should never mount without a preview, but if the resource
  // is briefly absent (e.g. a stale render), show a loading state instead of a
  // blank screen.
  if (!preview) {
    return <LoadingState title={strings.reviewLoading} />
  }

  const fullName = (row: BulkInviteRowResponse): string => {
    const name = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim()
    return name || strings.emptyValue
  }

  const toRow = (row: BulkInviteRowResponse): ReviewRowModel => {
    const canEditRole = row.previewCategory === 'VALID_NET_NEW'
    return {
      id: row.id,
      name: fullName(row),
      email: row.email ?? strings.emptyValue,
      role: canEditRole ? getSelectedInviteRole(row) : null,
      phone: row.phone ?? strings.emptyValue,
      category: row.previewCategory,
      canEditRole,
      roleWarningMessage: row.roleResolution?.warningMessage,
      // Surface the backend's specific skip reason(s) for invalid/clash rows.
      previewErrors: row.previewErrors,
    }
  }

  const tableRows = visibleCsvRows(preview).map(toRow)
  const summary = preview.summary ?? EMPTY_SUMMARY
  const conflictCount = summary.duplicate + summary.clash
  // Summary pills are the five invite-outcome counts from the spec. Colors
  // mirror the per-status pills in the table (ready=highlight, missing
  // data=pending, conflict=severe; the rest stay neutral). Zero-count buckets
  // are hidden (filtered at render) so the row only shows outcomes that
  // actually occurred. INFO_NOT_IN_FILE rows (org members absent from the CSV)
  // are not surfaced in this step at all — visibleCsvRows drops them from the
  // table and they have no summary pill.
  const summaryItems: Array<{
    label: string
    count: number
    color?: PillProps['color']
    icon: React.FC<React.SVGProps<SVGSVGElement>>
  }> = [
    {
      label: strings.catReady,
      count: summary.validNetNew,
      color: 'highlight',
      icon: SvgEmail,
    },
    {
      label: strings.catPendingInvite,
      count: summary.pendingInvite,
      icon: SvgCheckCircled,
    },
    {
      label: strings.catExistingMember,
      count: summary.existingMember,
      icon: SvgCheckCircled,
    },
    {
      label: strings.catInvalid,
      count: summary.invalid,
      color: 'pending',
      icon: SvgAlertCircled,
    },
    {
      label: strings.catConflict,
      count: conflictCount,
      color: 'severe',
      icon: SvgAlertCircled,
    },
  ]

  return (
    <div css={style.root}>
      {sendError && (
        <AlertPanel variant="severe">
          {strings.reviewSendError}
          {/* Prefer the backend's actionable message; fall back to generic. */}
          <div css={style.sendErrorDetail}>
            {sendErrorDetail ?? strings.reviewSendErrorDetail}
          </div>
        </AlertPanel>
      )}

      {/* role="group" so the aria-label is actually exposed — a label on a
          role-less div is ignored by most assistive tech. */}
      <div
        css={style.summaryPills}
        role="group"
        aria-label={strings.reviewSummaryLabel}
      >
        {summaryItems
          .filter((item) => item.count > 0)
          .map((item) => {
            const PillIcon = item.icon
            return (
              <Pill
                key={item.label}
                className={style.staticPillClassName}
                color={item.color}
                size="medium"
              >
                <span css={style.pillContent}>
                  <PillIcon
                    fill="currentColor"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                  />
                  {strings.reviewSummaryPill(item.label, item.count)}
                </span>
              </Pill>
            )
          })}
      </div>

      {tableRows.length > 0 ? (
        <ReviewTable
          rows={tableRows}
          availableRoles={availableInviteRoles}
          onRoleChange={setSelectedInviteRole}
          testId="bulkInviteReviewTable"
        />
      ) : (
        <div css={style.emptyCard}>{strings.reviewInvitedEmpty}</div>
      )}
    </div>
  )
}

export default ReviewStep
